import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useWaitlist } from "../context/WaitlistContext";

const CREAM = "#FAF7F2";
const LEAF = "#2F6B3F";
const LEAF_BRIGHT = "#6BA97C";
const MUTED = "#7C8F84";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useWaitlist();

  const goToSection = (id, close) => {
    if (close) close();
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/?scrollTo=${id}`);
    }
  };

  const goToStory = (close) => {
    if (close) close();
    navigate("/our-story");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="w-full sticky top-0 z-50 transition-all duration-500"
      style={{
        backgroundColor: scrolled ? "rgba(250,247,242,0.82)" : CREAM,
        backdropFilter: scrolled ? "blur(16px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(16px)" : "none",
      }}
    >
      {/* Row 1 */}
      <div className="flex items-center justify-between px-6 sm:px-10 lg:px-16 pt-7 pb-5 sm:pt-8 sm:pb-6">
        <a
          href="/"
          data-testid="header-logo"
          className="flex flex-col select-none"
          style={{ textDecoration: "none" }}
        >
          <span
            className="text-[8px] font-medium tracking-[0.22em] uppercase leading-none mb-[2px]"
            style={{ color: LEAF }}
          >
            Freshly
          </span>
          <span className="flex items-baseline">
            <span
              className="text-[2.592rem] sm:text-[2.938rem] leading-none tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, color: LEAF }}
            >
              Flavrd
            </span>
            <span
              className="text-[2.592rem] sm:text-[2.938rem] leading-none"
              style={{ color: LEAF_BRIGHT, fontFamily: "'Playfair Display', serif", fontWeight: 800 }}
            >
              .
            </span>
          </span>
        </a>

        <nav className="hidden sm:flex items-center gap-9">
          <NavLink testId="nav-link-products" label="Our Products" onClick={() => goToSection("range", null)} />
          <NavLink testId="nav-link-story" label="Our Story" onClick={() => goToStory(null)} />
          <button
            data-testid="header-waitlist-cta"
            onClick={openModal}
            className="ml-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all duration-300 hover:opacity-90 active:scale-95"
            style={{ backgroundColor: LEAF }}
          >
            Join Waitlist
          </button>
        </nav>

        <button
          className="sm:hidden p-1.5 rounded"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation"
          data-testid="mobile-menu-toggle"
          style={{ color: LEAF }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div style={{ borderTop: "1px solid rgba(47,107,63,0.12)" }} />

      {/* Row 2 — trust strip */}
      <div className="flex items-center justify-between px-6 sm:px-10 lg:px-16 py-2.5 overflow-hidden" style={{ backgroundColor: "#2A513C" }}>
        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "#FFFFFF", transform: "translateX(-10%)", display: "inline-block" }}>
          High in Protein
        </span>
        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "#FFFFFF", transform: "translateX(10%)", display: "inline-block" }}>
          Small Batches Only
        </span>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="sm:hidden overflow-hidden"
            style={{ backgroundColor: CREAM, borderTop: "1px solid rgba(47,107,63,0.10)" }}
          >
            <div className="flex flex-col px-6 py-4 gap-1">
              <MobileLink label="Our Products" testId="mobile-nav-link-products" onClick={() => goToSection("range", () => setOpen(false))} />
              <MobileLink label="Our Story" testId="mobile-nav-link-story" onClick={() => goToStory(() => setOpen(false))} />
              <div className="pt-3">
                <button
                  data-testid="mobile-waitlist-cta"
                  onClick={() => { openModal(); setOpen(false); }}
                  className="w-full py-3 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: LEAF }}
                >
                  Join the Waitlist
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function NavLink({ label, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="text-sm font-medium tracking-wide transition-colors duration-200 hover:opacity-60"
      style={{ color: "#16362A", background: "none", border: "none", cursor: "pointer" }}
    >
      {label}
    </button>
  );
}

function MobileLink({ label, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="text-left py-3 text-base font-medium border-b"
      style={{ color: "#16362A", borderColor: "rgba(47,107,63,0.10)", background: "none", cursor: "pointer", width: "100%" }}
    >
      {label}
    </button>
  );
}
